import React, { useCallback, useMemo } from "react"
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  List,
  Stack,
  Typography,
  useTheme,
} from "@mui/material"
import { get, size } from "lodash"
import { useNavigate, useParams } from "react-router-dom"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import clsx from "clsx"

import { getIcon } from "utils/icon"

import { getDurationDate } from "utils/date"

import { useImageModal } from "hooks/imageModal/useImageModal"
import { usePromotionDetail } from "hooks/promotion/usePromotionDetail"
import { useMobileUrl } from "hooks/useMobileUrl"
import { usePageSideBarLayout } from "providers/ui/hooks"
import { useTranslation } from "providers/i18n"
import { useFeatureDetail } from "hooks/feature"
import { useGeoLocation } from "providers/geolocation"

import { PageCard, PageCardContent } from "components/card"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-h"
import { Carousel, CarouselItem } from "components/Carousel"
import { EmptyData } from "components/EmptyData"
import { ContentWithAside } from "layouts/kiosk-h"
import { InfoListItem } from "components/ListItem"
import { LocationIcon, PercentIcon } from "components/icons"
import { LocationInfo } from "components/domains/features/LocationInfo"
import { QRCode } from "components/QRCode"
import { ImageCarouselModal } from "components/Image"
import { PlaceDetailSideBar } from "extensions/iconsiam/components/domains/features/PlaceDetailSideBar"
import { LeftToRightWrapper } from "components/LeftToRightWrapper"

export const OccupantPromotionDetailKioskHPage = ({
  Layout = ContentWithAside,
  AppNavigation = DefaultAppNavigation,
}) => {
  const navigate = useNavigate()
  const params = useParams()
  const { promotionId } = params
  const { t } = useTranslation()
  const { handleImage, openModal, activeImage, handleClose } = useImageModal()
  const { promotion } = usePromotionDetail(promotionId)
  const { dataLoaded, ...data } = useFeatureDetail(promotion?.locationId)
  const { mobileUrl } = useMobileUrl({
    path: `/promotions/${promotionId}`,
  })
  const { deviceLocation } = useGeoLocation()

  const isReadyToNavigate = !!deviceLocation && get(data, "isNavigatable", true)

  const theme = useTheme()
  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.promotions) || <PercentIcon />,
    [theme]
  )

  const { pageCardSideBar } =
    theme.components[`PlaceDetailPage`]?.styleOverrides || {}

  const {
    pageCard,
    pageCardContent,
    emptyDataContainer,
    card,
    infoListItem,
    cardContent,
    cardHeader,
    cardContentPrimaryText,
    cardContentSecondaryText,
    infoContainer,
    qrCodeContainer,
    qrCode,
    qrcodePrimaryText,
    qrcodeSecondaryText,
    cardContentTextWrapper,
    cardMediaWrapper,
    galleryCarouselWrapper,
    galleryCarousel,
    galleryCarouselItem,
    contentWrapper,
    galleryCarouselModal,
  } = theme.components?.[`VuiDetailCard`]?.styleOverrides || {}
  const { galleryCarouselOption } =
    theme.components[`VuiDetailCard`]?.defaultProps || {}

  const calendarIcon = useMemo(
    () => getIcon(theme?.assets?.calendarIcon) || <CalendarTodayIcon />,
    [theme]
  )

  const locationIcon = useMemo(
    () => getIcon(theme?.assets?.locationIcon) || <LocationIcon />,
    [theme]
  )

  const allImagesUrls = useMemo(
    () =>
      (size(promotion?.allImages) > 0
        ? promotion?.allImages?.map((v) => v?.url)
        : []) as string[],
    [promotion?.allImages]
  )

  const onNavigate = useCallback(() => {
    if (!isReadyToNavigate) return
    navigate(`/maps/dir/unknown/${data?.id}`)
  }, [navigate, isReadyToNavigate, data?.id])

  usePageSideBarLayout({
    sidebar: {
      open: true,
    },
  })

  return (
    <>
      <Layout
        aside={
          <PageCard sx={pageCardSideBar}>
            <PlaceDetailSideBar
              data={data}
              onNavigate={onNavigate}
              showAdditionalButtons={false}
            />
          </PageCard>
        }
      >
        <PageCard sx={pageCard}>
          <PageCardContent sx={pageCardContent}>
            {!promotion ? (
              <Box sx={emptyDataContainer}>
                <EmptyData
                  icon={emptyIcon}
                  primary={t("No Current Promotions")}
                  secondary={t("Check Back Soon")}
                />
              </Box>
            ) : (
              <Card
                elevation={0}
                sx={card}
                className={clsx("VuiDetailCard-card--occupant-promotion", {
                  "no-description": !promotion.descriptionText,
                })}
              >
                <Box sx={cardMediaWrapper}>
                  {promotion.allImages && (
                    <Box sx={galleryCarouselWrapper}>
                      <LeftToRightWrapper>
                        <Carousel
                          options={{
                            type: "fade",
                            padding: 0,
                            gap: 8,
                            pagination: true,
                            autoplay: true,
                            perPage: 1,
                            ...galleryCarouselOption,
                          }}
                          sx={galleryCarousel}
                        >
                          {promotion.allImages.map(({ id, url }) => {
                            return (
                              <CarouselItem
                                key={`slide-${id}`}
                                sx={galleryCarouselItem}
                              >
                                <CardMedia
                                  component="img"
                                  sx={galleryCarouselItem}
                                  src={url as string}
                                  onClick={() => handleImage(url)}
                                />
                              </CarouselItem>
                            )
                          })}
                        </Carousel>
                      </LeftToRightWrapper>
                    </Box>
                  )}
                </Box>
                <Box sx={contentWrapper}>
                  <CardHeader title={t(promotion.name)} sx={cardHeader} />
                  {promotion.showInfo && (
                    <List sx={infoContainer}>
                      {!promotion.showAlways && (
                        <InfoListItem
                          sx={infoListItem}
                          icon={calendarIcon}
                          primary={getDurationDate(
                            promotion.startDate,
                            promotion.endDate
                          )}
                          secondary={promotion.openingText}
                          direction="ltr"
                        />
                      )}
                      {promotion.hasLocation && (
                        <InfoListItem
                          sx={infoListItem}
                          icon={locationIcon}
                          primary={
                            <LocationInfo
                              feature={promotion.feature}
                              component="span"
                            />
                          }
                          secondary={t(promotion.boothName)}
                        />
                      )}
                    </List>
                  )}
                  <CardContent
                    sx={cardContent}
                    className="VuiDetailCard-cardContent"
                  >
                    <Box sx={cardContentTextWrapper}>
                      <Typography
                        variant="body1"
                        gutterBottom
                        sx={cardContentPrimaryText}
                      >
                        {t("Promotion Detail")}
                      </Typography>
                      <Typography
                        variant="body2"
                        className="VuiDetailCard-cardContentSecondaryText"
                        sx={cardContentSecondaryText}
                        dangerouslySetInnerHTML={{
                          __html: promotion.descriptionText,
                        }}
                      ></Typography>
                    </Box>
                  </CardContent>
                  {mobileUrl && (
                    <Box sx={qrCodeContainer}>
                      <QRCode sx={qrCode} url={mobileUrl} />
                      <Stack>
                        <Typography
                          variant="subtitle2"
                          component="p"
                          sx={qrcodePrimaryText}
                        >
                          {t("Scan Here")}
                        </Typography>
                        <Typography
                          variant="caption"
                          component="p"
                          sx={qrcodeSecondaryText}
                        >
                          {t("For Mobile")}
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                </Box>
              </Card>
            )}
          </PageCardContent>
        </PageCard>
      </Layout>
      <ImageCarouselModal
        className="VuiDetailCard-galleryCarouselModal--promotion"
        sx={galleryCarouselModal}
        activeSrc={activeImage}
        open={openModal}
        imageSrcs={allImagesUrls}
        onClose={handleClose}
        carouselOptions={{
          rewind: false,
        }}
      />
    </>
  )
}
